import type { InventorySnapshot } from '../domain/model';

export const FROZEN_SAVE_HOUSEHOLD_APPLICATION_ID = 'frozen-save' as const;
export const FROZEN_SAVE_HOUSEHOLD_STREAM_ID = 'inventory-v1' as const;

export interface SharedHouseholdSummary {
  readonly householdId: string;
  readonly joinedAt: string;
  readonly memberCount: number;
  readonly role: 'member' | 'owner';
}

export interface SharedHouseholdDevice {
  readonly keyId: string;
  readonly productSubjectId: string;
  readonly role: 'member' | 'owner';
  readonly current: boolean;
}

export type HouseholdSharingStatus =
  | { readonly state: 'standalone'; readonly reason: 'demo' | 'not-configured' }
  | { readonly state: 'disconnected' }
  | {
      readonly state: 'connected';
      readonly productSubjectId: string;
      readonly households: readonly SharedHouseholdSummary[];
      readonly binding: {
        readonly householdId: string;
        readonly keyReady: boolean;
        readonly keyVersion: number;
        readonly role: 'member' | 'owner';
      } | null;
    }
  | { readonly state: 'revoked' }
  | { readonly state: 'unavailable' };

export type HouseholdSyncResult =
  | { readonly state: 'published' | 'unchanged'; readonly checkpoint: number }
  | {
      readonly state: 'remote';
      readonly checkpoint: number;
      readonly snapshot: InventorySnapshot;
    }
  | { readonly state: 'conflict'; readonly checkpoint: number }
  | { readonly state: 'awaiting_key' }
  | { readonly state: 'revoked' | 'unavailable' };

export interface HouseholdAccountCreation {
  readonly status: HouseholdSharingStatus;
  readonly recoveryCode: string;
}

export interface HouseholdSharingPort {
  restoreFederatedSession?(): Promise<HouseholdSharingStatus>;
  readonly available: boolean;
  status(localHouseholdId: string | null): Promise<HouseholdSharingStatus>;
  signup(
    handle: string,
    email: string,
    passphrase: string,
    passphraseConfirmation: string,
  ): Promise<HouseholdAccountCreation>;
  login(handle: string, passphrase: string): Promise<HouseholdSharingStatus>;
  recover(
    handle: string,
    recoveryCode: string,
    passphrase: string,
    passphraseConfirmation: string,
  ): Promise<HouseholdAccountCreation>;
  deleteAccount(passphrase: string): Promise<void>;
  disconnect(): Promise<void>;
  create(snapshot: InventorySnapshot): Promise<HouseholdSharingStatus>;
  join(invitationToken?: string): Promise<HouseholdSharingStatus>;
  invitationLink(householdId: string): Promise<{ readonly expiresAt: string; readonly url: string }>;
  devices(householdId: string): Promise<readonly SharedHouseholdDevice[]>;
  authorizeDevices(householdId: string): Promise<number>;
  synchronize(snapshot: InventorySnapshot, preferRemote?: boolean): Promise<HouseholdSyncResult>;
  confirmRemoteApplied(snapshot: InventorySnapshot, checkpoint: number): Promise<void>;
  revokeMember(householdId: string, productSubjectId: string, snapshot: InventorySnapshot): Promise<void>;
  revokeCurrentDevice(): Promise<void>;
}
